import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

/**
 * Icons import.
 */
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons'

/**
 * Google Tag Manager Plugin import.
 */
import TagManager from 'react-gtm-module';

/**
 * Store config.
 */
import configStore from 'store/config';

/**
 * API import.
 */
import { api, interceptors } from 'src/utils/api';

/**
 * Error handler import.
 */
import ErrorBoundary from 'components/error-boundary';

/**
 * Third parties components.
 */
import { ToastContainer, ToastContainerProps } from 'react-toastify';

/**
 * Root containers import.
 */
import Header from './header';
import AppRoutes from './routes/appRoutes';

/**
 * Styles import.
 */
import 'react-toastify/dist/ReactToastify.css';
import 'src/scss/custom-toast.scss';

/* Font Awesome Icons */
library.add(fas, fab);

/* Google Tag Manager init */
TagManager.initialize({
    gtmId: process.env.GTM_ID,
    auth: process.env.GTM_AUTH,
    preview: process.env.GTM_PREVIEW,
});

/* Redux store boot */
const { reduxStore, persistor, history } = configStore(undefined, api);
export const store = reduxStore;

/* Interceptors configuration */
interceptors(store, history);

/**
 * Toast Settings.
 */
const toastSettings: ToastContainerProps = {
    className: 'custom-toast-container',
    toastClassName: 'custom-toast',
    progressClassName: 'custom-toast-progress',
    position: 'bottom-center',
    autoClose: 3000,
    closeButton: false,
    hideProgressBar: true,
};

/**
 * Root component.
 */
const Root = () => {
    const urlParams = new URLSearchParams(window.location.search);

    useEffect(() => {
        if (urlParams.get('utm_source')) {
            localStorage.setItem('utm_source', urlParams.get('utm_source'));
        }

        if (urlParams.get('utm_medium')) {
            localStorage.setItem('utm_medium', urlParams.get('utm_medium'));
        }

        if (urlParams.get('utm_campaign')) {
            localStorage.setItem('utm_campaign', urlParams.get('utm_campaign'));
        }
    }, [urlParams]);

    return (
        <React.Fragment>
            <Header />
            <ErrorBoundary>
                <Provider store={reduxStore}>
                    <PersistGate loading={null} persistor={persistor}>
                        <AppRoutes history={history} />
                    </PersistGate>
                </Provider>
            </ErrorBoundary>
            <ToastContainer {...toastSettings} />
        </React.Fragment>
    );
};

export default Root;
